<template>
  <div class="component-wrapper" id="application-detail">
    <DetailsHeaderPEL>
      <template #fullName>
        <h2
          class="applicant-name fullname-wrap text-overflow-ellipsis"
          :title="summary.primaryApplicantFullName"
          v-title-overflow
        >
          {{ summary.primaryApplicantFullName }}
        </h2>
      </template>
      <template #statusDropdown>
        <application-status-container />
      </template>
      <template #statusStepper>
        <details-stepper></details-stepper>
      </template>
      <template #summary>
        <div class="application-summary-wrapper" style="">
          <div class="application-summary section-1">
            <div class="summary-items">
              <div class="summary-item r1">
                <div class="cu-program-container">
                  <div class="label">Credit Union Program</div>
                  <div class="cu-program-name">
                    <span
                      class="lender-name"
                      :title="lenderName"
                      v-title-overflow
                    >
                      {{ lenderName }}</span
                    >
                    <span class="product-abbreviation"
                      >&nbsp;-&nbsp;{{ productAbbreviation }}</span
                    >
                  </div>
                </div>
              </div>
              <div class="summary-item r1">
                <div class="school-container">
                  <div class="label">School</div>
                  <div
                    class="school-name"
                    :title="summary.schoolName"
                    v-title-overflow
                  >
                    {{ summary.schoolName }}
                  </div>
                </div>
              </div>
              <div
                class="summary-item"
                v-for="(field, index) in summaryFields"
                :key="index"
                style=""
              >
                <div class="label">{{ field.title }}</div>
                <div class="content">{{ field.value }}</div>
              </div>
            </div>
          </div>
          <div class="application-summary section-2">
            <div class="summary-items">
              <div
                class="summary-item"
                v-for="(field, index) in summaryFields"
                :key="index"
                style=""
              >
                <div class="label">{{ field.title }}</div>
                <div class="content">{{ field.value }}</div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </DetailsHeaderPEL>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useApplicationStore } from "@/store/store/applicationVMStore";
import DetailsStepper from "@/components/v2/application/DetailsStepper.vue";
import DetailsHeaderPEL from "@/components/v2/application/details/DetailsHeaderPEL.vue";
import ApplicationStatusContainer from "@/components/v2/application/ApplicationStatusContainer.vue";

const applicationStore = useApplicationStore();

const { summary } = storeToRefs(applicationStore);

const lenderName = computed<string>(
  () => applicationStore.application?.detailViewModel.lenderName || ""
);

const productAbbreviation = computed<string>(() =>
  summary.value.lenderName.split("-")[1].trim()
);

const maxLoanAmount = computed(
  () => applicationStore.summary.maxApprovedLimitDisplay
);

const summaryFields = computed(() => {
  return [
    {
      title: "Loan Amount",
      value: summary.value.loanAmountDisplay,
    },
    {
      title: "Max Approved",
      value: maxLoanAmount.value,
    },
    {
      title: "Interest Rate",
      value: `${summary.value.rate} / ${summary.value.rateTypeName}`,
    },
    {
      title: "Anticipated Grad Date",
      value: `${summary.value.graduationDate}`,
    },
    {
      title: "Requested Term",
      value: `${summary.value.academicTerm}`,
    },
  ];
});
</script>
<style scoped>
* {
  font-family: "Montserrat", arial, sans-serif;
}

.applicant-name {
  margin-bottom: 0;
  font-size: 26px;
}

.applicant-name h2 {
  display: block !important;
}
.component-wrapper#application-detail {
  width: 100%;
  background-color: #e2e4e9;
  padding-left: 22px;
  padding-right: 22px;
  padding-top: 0;
  padding-bottom: 0;
}

div.xpage-top-right {
  padding: 0;
  white-space: wrap;
}

div .xpage-top {
  padding: 22px 0px 0px 19px;
}

.xpage-top.xpage-top-col1 {
  border: none;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.xpage-top-right {
  width: 55%;
  display: flex;
  flex-direction: column;
}

.application-detail-details-applicant {
  width: calc(100% - 500px);
  margin-bottom: 15px;
}

.application-detail-details-stepper {
  width: 500px;
  height: 108px;
  position: absolute;
  top: 22px;
  right: 0;
}

.cu-program-container .cu-program-name {
  display: flex;
}

.cu-program-container .cu-program-name .lender-name {
  max-width: 72%;
}

.school-container .school-name {
  max-width: 100%;
}

.cu-program-container .cu-program-name .lender-name,
.school-container .school-name {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cu-program-container .cu-program-name .lender-name,
.cu-program-container .cu-program-name .product-abbreviation,
.school-container .school-name,
.application-summary .summary-item .content {
  color: #000;
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  line-height: normal;
}

.product-abbreviation {
  min-width: 62.4336px;
}

div.label {
  color: #000;
  font-family: var(--sc-montserrat-font-family);
  font-size: 14px;
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: normal;
  text-transform: uppercase;
  margin-bottom: 5px;
}

.application-summary {
  border: none;
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 0px 0px 0px 22px;
  column-gap: 34px;
  row-gap: 22px;
  flex-wrap: wrap;
}

.application-summary-wrapper {
  border: none;
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 0px 0px 0px 22px;
  column-gap: 34px;
  row-gap: 22px;
  flex-wrap: wrap;
  position: relative;
}

.application-summary-wrapper .application-summary-details {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.application-summary-wrapper {
  flex-direction: column;
  padding: 22px 0 0 0;
}

.application-summary-wrapper .application-summary {
  padding: 0;
}

.application-summary-wrapper .application-summary.section-1 {
  width: calc(100% - 500px);
  display: flex;
  flex-direction: row;
}

.application-summary-wrapper .application-summary.section-1 .summary-items {
  display: flex;
  width: 100%;
  column-gap: 50px;
}
.application-summary-wrapper
  .application-summary.section-1
  .summary-items
  .summary-item:is(.r1) {
  max-width: calc(100% / 2);
  overflow: hidden;
}

.application-summary-wrapper
  .application-summary.section-1
  .summary-items
  .summary-item:not(.r1) {
  opacity: 0;
  display: none;
  visibility: hidden;
  max-width: calc(100% / 3);
  flex-direction: column;
}
.application-summary-wrapper .application-summary.section-2 {
  width: 100%;
  margin-bottom: 20px;
}

.application-summary-wrapper .application-summary.section-2 .summary-items {
  display: flex;
  width: 100%;
  column-gap: 50px;
}

.application-summary-wrapper
  .application-summary.section-2
  .summary-items
  .summary-item {
  max-width: calc(100% / 3);
}

@media screen and (min-width: 1500px) {
  .cu-program-container .cu-program-name .lender-name {
    max-width: 80%;
  }

  .application-summary-wrapper .application-summary.section-1 {
    width: calc(100% - 500px - 22px);
  }
  .application-summary {
    padding-bottom: 22px;
  }

  .application-summary-wrapper .application-summary.section-1 .summary-items {
    flex-wrap: wrap;
    row-gap: 22px;
    margin-bottom: 20px;
  }

  .application-summary-wrapper
    .application-summary.section-1
    .summary-items
    .summary-item:is(.r1) {
		max-width: 100%;
  }

  .application-summary-wrapper .application-summary.section-2 {
    opacity: 0;
    display: none;
    visibility: hidden;
  }

  .application-summary-wrapper
    .application-summary.section-1
    .summary-items
    .summary-item:not(.r1) {
    opacity: 1;
    visibility: visible;
    max-width: calc(100% / 3);
    display: flex;
  }
}
@media screen and (min-width: 1400px) {
  .cu-program-container .cu-program-name .lender-name {
    max-width: 80%;
  }
}

@media screen and (max-width: 1200px) {
  .application-summary-wrapper
    .application-summary.section-1
    .summary-items
    .summary-item:is(.r1) {
    max-width: calc(100% / 2);
  }

  .application-summary-wrapper
    .application-summary.section-1
    .summary-items
    .summary-item:is(.r1)
    .school-container {
    max-width: 100%;
  }

  .cu-program-container,
  .school-container {
    max-width: none;
  }

  .application-detail-details-applicant {
    width: 100%;
  }
}

@media screen and (max-width: 1150px) {
  .application-detail-details-stepper {
    position: relative;
    top: 0;
  }

  .product-abbreviation {
    min-width: 65px;
  }

  .application-summary-wrapper {
    padding-top: 0;
  }

  .application-summary-wrapper .application-summary.section-1 {
    width: 100%;
  }
  .application-summary-wrapper .application-summary.section-1 .summary-items {
    flex-wrap: wrap;
    row-gap: 22px;
    margin: 20px 0;
  }

  .application-summary-wrapper
    .application-summary.section-1
    .summary-items
    .summary-item:is(.r1) {
    max-width: calc(100% / 3);
  }

  .application-summary-wrapper .application-summary.section-2 {
    opacity: 0;
    display: none;
    visibility: hidden;
  }

  .application-summary-wrapper
    .application-summary.section-1
    .summary-items
    .summary-item:not(.r1) {
    opacity: 1;
    visibility: visible;
    max-width: calc(100% / 3);
    display: flex;
  }
}

@media screen and (max-width: 992px) {
  .application-summary {
    flex-wrap: wrap;
  }
  .summary-item:is(.r1) .cu-program-container .cu-program-name {
    display: block;
  }

  .school-container .school-name {
    text-wrap: auto;
  }
  .summary-item:is(.r1) .cu-program-container .cu-program-name .lender-name,
  .school-container .school-name {
    max-width: 100%;
    text-wrap: wrap;
  }
}

@media screen and (max-width: 960px) {
  .application-summary-wrapper {
    padding-left: 0;
  }
}

@media screen and (max-width: 800px) {
  .cu-program-container .cu-program-name .lender-name {
    max-width: 60%;
  }
  .school-container {
    width: 100%;
  }
}

@media screen and (max-width: 768px) {
  div .xpage-top {
    padding: 22px 0 0 0;
  }
  .xpage-top.xpage-top-col1 {
    flex-direction: column;
  }

  .application-summary {
    padding: 22px 0 0 0;
  }
  .xpage-top-right {
    width: 100%;
  }

  .application-detail-details-stepper {
    position: relative;
    top: 0;
    width: 100%;
  }
  .application-detail-details-applicant,
  .application-detail-details-stepper {
    width: 100%;
  }

  .cu-program-container,
  .school-container {
    max-width: none;
  }

  .cu-program-container .cu-program-name .lender-name {
    max-width: 80%;
  }

  .application-summary .summary-item {
    min-width: 45%;
  }

  .product-abbreviation {
    min-width: 75px;
  }
}

@media screen and (max-width: 768px) {
  .application-summary-wrapper {
    padding: 0;
  }
  .application-summary-wrapper
    .application-summary.section-1
    .summary-items
    .summary-item:is(.r1),
  .application-summary-wrapper
    .application-summary.section-1
    .summary-items
    .summary-item:not(.r1) {
    max-width: 100%;
  }
}
</style>
